import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Coins, ShoppingBag, Shield, Sparkles, Zap, Crown, Package, History, ChevronRight, X } from "lucide-react";

const CATEGORY_META = {
    token: { label: "Tokens", icon: Shield, color: "#39FF14" },
    frame: { label: "Frames", icon: Sparkles, color: "#FF3B30" },
    cosmetic: { label: "Titles", icon: Crown, color: "#F59E0B" },
    booster: { label: "Boosters", icon: Zap, color: "#22d3ee" },
    merch: { label: "Merch", icon: Package, color: "#a78bfa" },
};

export default function Shop() {
    const { user, refresh } = useAuth();
    const [balance, setBalance] = useState(0);
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [buying, setBuying] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [inventory, setInventory] = useState([]);

    const load = async () => {
        const [b, i, inv] = await Promise.all([
            client.get("/users/me/balance"),
            client.get("/shop/items"),
            client.get("/users/me/inventory"),
        ]);
        setBalance(b.data.noex_balance || 0);
        setItems(i.data || []);
        setInventory(inv.data || []);
    };

    useEffect(() => {
        load();
    }, []);

    const openHistory = async () => {
        const h = await client.get("/users/me/noex/history");
        setHistory(h.data || []);
        setShowHistory(true);
    };

    const confirmBuy = async () => {
        if (!selected) return;
        setBuying(true);
        try {
            await client.post(`/shop/items/${selected.id}/purchase`);
            toast.success(`${selected.name} acquired.`);
            setSelected(null);
            await load();
            if (refresh) await refresh();
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setBuying(false);
        }
    };

    const cats = ["all", ...Object.keys(CATEGORY_META)];
    const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

    return (
        <div className="px-5 pt-8 pb-28" data-testid="shop-page">
            {/* Header */}
            <div className="flex items-end justify-between">
                <div>
                    <div className="label-cap text-xs text-zinc-500">Shop</div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight leading-none">
                        Spend $NoEx
                    </h1>
                </div>
                <button
                    onClick={openHistory}
                    data-testid="shop-history-btn"
                    className="surface-2 px-3 py-2 flex items-center gap-1.5 text-xs text-zinc-300 hover:text-neon-green"
                >
                    <History size={14} /> History
                </button>
            </div>

            {/* Balance card */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 surface-2 p-5 relative overflow-hidden"
                data-testid="shop-balance-card"
                style={{
                    background: "linear-gradient(135deg, #0a160a 0%, #0d0d0d 70%)",
                    boxShadow: "0 0 32px rgba(57,255,20,0.15), inset 0 0 0 1px rgba(57,255,20,0.4)",
                }}
            >
                <div className="absolute -right-8 -top-8 w-40 h-40 bg-neon-green/10 rounded-full blur-3xl" />
                <div className="relative flex items-center gap-4">
                    <div className="w-14 h-14 bg-neon-green/20 border border-neon-green flex items-center justify-center">
                        <Coins size={26} className="text-neon-green" />
                    </div>
                    <div className="flex-1">
                        <div className="label-cap text-[10px] text-neon-green">Your Balance</div>
                        <div className="font-display font-black text-4xl text-neon-green leading-none mt-1" data-testid="shop-balance-value">
                            {balance.toLocaleString()}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">$NoEx — earn more by training</div>
                    </div>
                </div>
            </motion.div>

            {/* Category filter chips */}
            <div className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {cats.map((c) => {
                    const m = CATEGORY_META[c];
                    const active = filter === c;
                    return (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            data-testid={`shop-filter-${c}`}
                            className={`shrink-0 label-cap text-[10px] px-3 py-1.5 border transition-all ${
                                active
                                    ? "bg-neon-green text-black border-neon-green"
                                    : "text-zinc-400 border-charcoal-500 hover:text-white"
                            }`}
                        >
                            {c === "all" ? "All" : m?.label || c}
                        </button>
                    );
                })}
            </div>

            {/* Items grid */}
            <div className="mt-4 grid grid-cols-2 gap-3">
                {filtered.length === 0 && (
                    <div className="col-span-2 surface-1 p-8 text-center text-zinc-500 text-sm">
                        No items in this category.
                    </div>
                )}
                {filtered.map((it, idx) => {
                    const m = CATEGORY_META[it.category] || CATEGORY_META.cosmetic;
                    const Icon = m.icon;
                    const owned = inventory.some(
                        (p) => p.item_id === it.id && (p.item_category !== "merch")
                    );
                    const affordable = balance >= it.price_noex;
                    return (
                        <motion.button
                            key={it.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            onClick={() => setSelected(it)}
                            data-testid={`shop-item-${it.id}`}
                            className="surface-2 p-4 text-left relative overflow-hidden hover:border-neon-green transition-all group"
                        >
                            <div
                                className="absolute -right-4 -top-4 w-20 h-20 rounded-full blur-2xl opacity-50"
                                style={{ background: m.color }}
                            />
                            <div className="relative">
                                <div
                                    className="w-12 h-12 border flex items-center justify-center mb-3"
                                    style={{ borderColor: m.color, background: `${m.color}15` }}
                                >
                                    <Icon size={22} style={{ color: m.color }} />
                                </div>
                                <div className="label-cap text-[9px]" style={{ color: m.color }}>
                                    {m.label}
                                </div>
                                <div className="font-display font-black text-sm uppercase tracking-tight leading-tight mt-1">
                                    {it.name}
                                </div>
                                <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">
                                    {it.description}
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <div className="flex items-center gap-1 text-neon-green font-bold text-sm">
                                        <Coins size={12} /> {it.price_noex}
                                    </div>
                                    {owned ? (
                                        <span className="label-cap text-[9px] text-neon-green">Owned</span>
                                    ) : !affordable ? (
                                        <span className="label-cap text-[9px] text-zinc-600">Locked</span>
                                    ) : (
                                        <ChevronRight size={14} className="text-zinc-500 group-hover:text-neon-green" />
                                    )}
                                </div>
                            </div>
                        </motion.button>
                    );
                })}
            </div>

            {/* Inventory section */}
            {inventory.length > 0 && (
                <div className="mt-8">
                    <div className="flex items-end justify-between mb-3">
                        <div>
                            <span className="label-cap text-xs text-zinc-500">Your stash</span>
                            <h2 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
                                Inventory
                            </h2>
                        </div>
                    </div>
                    <div className="space-y-2" data-testid="inventory-list">
                        {inventory.slice(0, 20).map((p) => {
                            const m = CATEGORY_META[p.item_category] || CATEGORY_META.cosmetic;
                            const Icon = m.icon;
                            return (
                                <div
                                    key={p.id}
                                    className="surface-1 p-3 flex items-center gap-3"
                                    data-testid={`inventory-${p.id}`}
                                >
                                    <div
                                        className="w-10 h-10 border flex items-center justify-center shrink-0"
                                        style={{ borderColor: m.color }}
                                    >
                                        <Icon size={16} style={{ color: m.color }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold truncate">{p.item_name}</div>
                                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                                            {p.status} · {new Date(p.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-neon-green">
                                        -{p.price_paid}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Confirm purchase modal */}
            {selected && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={() => setSelected(null)}
                    data-testid="purchase-modal"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md surface-2 p-6 relative"
                    >
                        <button
                            onClick={() => setSelected(null)}
                            data-testid="purchase-close"
                            className="absolute top-3 right-3 text-zinc-500 hover:text-white"
                        >
                            <X size={20} />
                        </button>
                        <div className="label-cap text-[10px] text-neon-green">Confirm Purchase</div>
                        <div className="font-display font-black text-2xl uppercase mt-2">
                            {selected.name}
                        </div>
                        <div className="text-sm text-zinc-400 mt-2">{selected.description}</div>
                        <div className="mt-6 surface-1 p-4 flex items-center justify-between">
                            <span className="text-xs text-zinc-500 uppercase tracking-widest">Cost</span>
                            <span className="flex items-center gap-1 font-display font-black text-2xl text-neon-green">
                                <Coins size={20} /> {selected.price_noex.toLocaleString()}
                            </span>
                        </div>
                        <div className="mt-2 text-xs text-zinc-500 flex justify-between">
                            <span>Your balance</span>
                            <span>{balance.toLocaleString()} → {(balance - selected.price_noex).toLocaleString()}</span>
                        </div>
                        <button
                            disabled={buying || balance < selected.price_noex}
                            onClick={confirmBuy}
                            data-testid="purchase-confirm"
                            className="mt-5 btn-neon w-full py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {buying ? "Processing..." : balance < selected.price_noex ? "Not Enough $NoEx" : `Buy for ${selected.price_noex}`}
                        </button>
                        {selected.category === "merch" && (
                            <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                                Merch is fulfilled by an admin. After purchase you'll be contacted via your account email for shipping.
                            </div>
                        )}
                    </motion.div>
                </div>
            )}

            {/* History drawer */}
            {showHistory && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end justify-center"
                    onClick={() => setShowHistory(false)}
                    data-testid="history-modal"
                >
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-charcoal-900 border-t border-charcoal-500 p-5 max-h-[70vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="label-cap text-[10px] text-zinc-500">Last 100 transactions</div>
                                <div className="font-display font-black text-xl uppercase">$NoEx History</div>
                            </div>
                            <button onClick={() => setShowHistory(false)} className="text-zinc-500">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-1.5">
                            {history.length === 0 && (
                                <div className="text-center text-zinc-500 text-sm py-8">No transactions yet.</div>
                            )}
                            {history.map((h) => (
                                <div key={h.id} className="surface-1 p-3 flex items-center justify-between text-xs">
                                    <div className="min-w-0">
                                        <div className="font-bold uppercase tracking-wide truncate">{h.reason.replace(/_/g, " ")}</div>
                                        <div className="text-zinc-500 text-[10px]">
                                            {new Date(h.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className={`font-bold ${h.delta > 0 ? "text-neon-green" : "text-neon-orange"}`}>
                                        {h.delta > 0 ? "+" : ""}{h.delta}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
